import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import ActionBar from "../../common/ActionBar";
import styled from "styled-components";
import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";
import { navigate } from "@reach/router";
import _ from "lodash";
import { Formik } from "formik";
import { PrimaryCTAButton } from "../../common/Buttons";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import SystemService from "../../../services/SystemService";
import { getErrorMessage, getFileName } from "../../../helpers/functions";
import { FormLabel } from "@material-ui/core";
import { CustomInputField, CustomFileUpload } from "../../common/FormInputs";
import labels from "../../../helpers/labels.json";
import AWSService from "../../../services/AWSService";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { makeStyles } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    backgroundColor: "#ffffff",
    border: "1px solid #e8ecf1",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    margin: "16px 0px 0px !important",
    borderRadius: "8px !important",
    transition: "all 0.3s ease !important",
    "&:hover": {
      boxShadow: "0 4px 16px rgba(0, 131, 140, 0.12)",
    },
    "& .MuiAccordionSummary-content": {
      margin: "16px 0 !important",
    },
    "& .MuiIconButton-root": {
      padding: 8,
      marginRight: 0,
      backgroundColor: "transparent",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "rgba(0, 131, 140, 0.1)",
      },
    },
    "& .MuiSvgIcon-root": {
      fill: "#00838c",
      transition: "all 0.3s ease",
    },
  },
}));

// Isolated input field component with local state
const MediatorInputField = React.memo(
  ({
    label,
    name,
    initialValue,
    onChange,
    onBlur,
    error,
    touched,
    type = "text",
    placeholder = "",
  }) => {
    const [localValue, setLocalValue] = useState(initialValue || "");
    const [isFocused, setIsFocused] = useState(false);
    const timeoutRef = useRef(null);

    useEffect(() => {
      setLocalValue(initialValue || "");
    }, [initialValue]);

    const handleChange = useCallback(
      (e) => {
        const newValue = e.target.value;
        setLocalValue(newValue);

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          onChange(e);
        }, 50);
      },
      [onChange]
    );

    const handleBlur = useCallback(
      (e) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        onChange(e);
        onBlur(e);
        setIsFocused(false);
      },
      [onChange, onBlur]
    );

    const handleFocus = useCallback(() => {
      setIsFocused(true);
    }, []);

    return (
      <FieldWrapper>
        <FieldLabel
          isFocused={isFocused}
          hasValue={!!localValue}
          hasError={error && touched}
        >
          {_.upperCase(label)}
        </FieldLabel>
        <FieldInputWrapper isFocused={isFocused} hasError={error && touched}>
          <CustomInputField
            variant="outlined"
            className="input-white"
            autoComplete="off"
            spellCheck="false"
            type={type}
            value={localValue}
            id={name}
            name={name}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            placeholder={placeholder}
            isFocused={isFocused}
            hasError={error && touched}
          />
          {error && touched && <ErrorIndicator>!</ErrorIndicator>}
        </FieldInputWrapper>
        {error && touched && <ErrorMessage>{error}</ErrorMessage>}
      </FieldWrapper>
    );
  }
);

MediatorInputField.displayName = "MediatorInputField";

// File Upload Field
const MediatorFileField = React.memo(
  ({
    label,
    name,
    value,
    onChange,
    onBlur,
    error,
    touched,
    setFile,
    accept,
  }) => {
    const [isFocused, setIsFocused] = useState(false);

    return (
      <FieldWrapper>
        <FieldLabel
          isFocused={isFocused}
          hasValue={!!value}
          hasError={error && touched}
        >
          {_.upperCase(label)}
        </FieldLabel>
        <FileUploadWrapper
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          isFocused={isFocused}
          hasError={error && touched}
        >
          <CustomFileUpload
            type="file"
            value={value}
            file={{ name: getFileName(value) }}
            error={error && touched}
            setFile={setFile}
            accept={accept}
            onChange={onChange}
            onBlur={onBlur}
            style={{ width: "100%" }}
          />
        </FileUploadWrapper>
        {error && touched && <ErrorMessage>{error}</ErrorMessage>}
      </FieldWrapper>
    );
  }
);

MediatorFileField.displayName = "MediatorFileField";

// Highly Optimized Section Component
const MediatorSection = React.memo(
  ({
    section,
    secIndex,
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldError,
    handleRemove,
    s3Upload,
    classes,
    totalSections,
  }) => {
    const [expanded, setExpanded] = useState(secIndex === 0);

    const inputFields = [
      { name: "name", label: labels.neutral_name, placeholder: "Enter name" },
      {
        name: "email",
        label: labels.profile_email,
        type: "email",
        placeholder: "Enter email",
      },
      {
        name: "date",
        label: "Date",
        type: "date",
      },
      {
        name: "qualification",
        label: labels.neutral_qualification,
        placeholder: "e.g., B.A., M.B.A.",
      },
      {
        name: "lastHeldPosition",
        label: labels.neutral_lastHeldPosition,
        placeholder: "e.g., Senior Manager",
      },
      {
        name: "yearsOfExperience",
        label: labels.neutral_yearsOfExperience,
        placeholder: "e.g., 10 years",
      },
      {
        name: "languagesKnown",
        label: labels.neutral_languagesKnown,
        placeholder: "e.g., English, Hindi, Tamil",
      },
      {
        name: "affiliatedOrganisation",
        label: labels.neutral_affiliatedOrganisation,
        placeholder: "Organization name",
      },
      {
        name: "areasOfExpertise",
        label: labels.neutral_areasOfExpertise,
        placeholder: "e.g., Commercial, Finance",
      },
      {
        name: "category",
        label: labels.neutral_category,
        placeholder: "e.g., Senior, Junior",
      },
      {
        name: "location",
        label: labels.neutral_location,
        placeholder: "City, Country",
      },
      {
        name: "chronicles",
        label: labels.neutral_chronicles,
        placeholder: "Brief background",
      },
      {
        name: "otherInterests",
        label: labels.neutral_otherInterests,
        placeholder: "Other interests",
      },
    ];

    const handleFileUpload = useCallback(
      (file) => {
        s3Upload(
          file,
          `sections[${secIndex}].image`,
          setFieldValue,
          setFieldError
        );
      },
      [secIndex, s3Upload, setFieldValue, setFieldError]
    );

    const handleRemoveClick = useCallback(() => {
      handleRemove(values.sections, secIndex, setFieldValue);
    }, [secIndex, values.sections, handleRemove, setFieldValue]);

    return (
      <AccordionWrapper
        className={classes.root}
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
      >
        <StyledAccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`panel${secIndex}-content`}
          id={`panel${secIndex}-header`}
        >
          <SummaryContent>
            <SummaryIcon>
              <EditIcon fontSize="small" />
            </SummaryIcon>
            <SummaryTexts>
              <SummaryTitle>
                {section.name || `Mediator ${secIndex + 1}`}
              </SummaryTitle>
              <SummarySubtitle>ID: {section._id || "New"}</SummarySubtitle>
            </SummaryTexts>
            <SummaryBadge>{secIndex + 1}</SummaryBadge>
          </SummaryContent>
        </StyledAccordionSummary>

        <StyledAccordionDetails>
          <Section>
            <SectionHeader>
              <SectionTitle>
                {_.upperCase(labels.mediator_item)} #{secIndex + 1}
              </SectionTitle>
              {totalSections > 1 && (
                <RemoveButton
                  onClick={handleRemoveClick}
                  title="Remove mediator"
                  type="button"
                >
                  <DeleteIcon style={{ fill: "#d91b1b" }} fontSize="small" />
                </RemoveButton>
              )}
            </SectionHeader>

            <FieldsGrid>
              {/* Profile Image */}
              <MediatorFileField
                label={labels.neutral_image}
                name={`sections[${secIndex}].image`}
                value={section.image}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.sections?.[secIndex]?.image}
                touched={touched.sections?.[secIndex]?.image}
                setFile={handleFileUpload}
                accept="image/*"
              />

              {/* Input Fields */}
              {inputFields.map((field) => {
                const fieldName = `sections[${secIndex}].${field.name}`;
                const fieldValue = section[field.name];
                const fieldError = errors.sections?.[secIndex]?.[field.name];
                const fieldTouched = touched.sections?.[secIndex]?.[field.name];

                return (
                  <MediatorInputField
                    key={fieldName}
                    label={field.label}
                    name={fieldName}
                    initialValue={fieldValue}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={fieldError}
                    touched={fieldTouched}
                    type={field.type}
                    placeholder={field.placeholder}
                  />
                );
              })}
            </FieldsGrid>
          </Section>
        </StyledAccordionDetails>
      </AccordionWrapper>
    );
  }
);

MediatorSection.displayName = "MediatorSection";

const Index = () => {
  const breadcrumbs = ["Management tools", labels.update_mediators_info];
  const { setLoader } = useLoader();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [systemConfig, setSystemConfig] = useState({
    mobile: "",
    tutorialUrl: "",
    faq: [],
    terminology: [],
    tnc: [],
    mailBodyContent: [],
    mediatorsInfo: [],
  });
  const formikRef = useRef();

  const defaultMediatorObject = useMemo(
    () => ({
      _id: "",
      name: "",
      email: "",
      date: "",
      image: "",
      qualification: "",
      lastHeldPosition: "",
      yearsOfExperience: "",
      languagesKnown: "",
      affiliatedOrganisation: "",
      areasOfExpertise: "",
      category: "",
      location: "",
      chronicles: "",
      otherInterests: "",
    }),
    []
  );

  useEffect(() => {
    async function getSystemConfig() {
      try {
        setLoader({ state: true, message: "Fetching Configurations..." });
        const response = await SystemService.getConfigAuth();
        if (response.systemConfig) {
          setSystemConfig(response.systemConfig);
        }
      } catch (error) {
        const message = getErrorMessage(error);
        enqueueSnackbar(message, { variant: "error" });
      } finally {
        setLoader({ state: false });
      }
    } 
    getSystemConfig();  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 

  const s3Upload = useCallback(
    async (file, field, setFieldValue, setFieldError) => {
      if (file.type.includes("image/")) {
        try {
          setLoader({ state: true, message: `Uploading: ${file.name}` });
          const payload = { key: file.name };
          const s3Response = await AWSService.getS3URL(payload);
          if (s3Response.url) {
            const res = await AWSService.uploadToS3(s3Response.url, file);
            setFieldValue(field, res);
            enqueueSnackbar("Image uploaded successfully", {
              variant: "success",
            });
          }
        } catch (error) {
          setFieldError(field, "Could not upload this file");
          enqueueSnackbar("Failed to upload image", { variant: "error" });
        } finally {
          setLoader({ state: false });
        }
      } else {
        enqueueSnackbar("Please upload a valid image file", {
          variant: "error",
        });
      }
    },
    [setLoader, enqueueSnackbar]
  );

  const onBreadcrumbClick = useCallback((selected) => {
    if (_.snakeCase(selected) === "management_tools") {
      navigate("/dashboard/case-manager/tools");
    }
  }, []);

  const addSection = useCallback(
    (setFieldValue, sections) => {
      const newSections = [
        ...sections,
        { ...defaultMediatorObject, _id: Date.now().toString() },
      ];
      setFieldValue("sections", newSections);
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultMediatorObject, enqueueSnackbar]
  );

  const handleRemove = useCallback(
    (sections, index, setFieldValue) => {
      const returnData = sections.filter((_, id) => id !== index);
      setFieldValue("sections", returnData);
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueSnackbar]
  );

  const onFormSubmit = useCallback(
    async (values) => {
      try {
        setLoader({ state: true, message: "Updating Mediators Info..." });
        const payload = {
          ...systemConfig,
          mediatorsInfo: values.sections?.map((el) => ({
            ...el,
            date: el?.date || "",
            languagesKnown: el?.languagesKnown
              ?.toString()
              .split(",")
              .map((item) => item.trim()),
          })),
        };
        const response = await SystemService.setConfig(payload);
        if (response.config) {
          setSystemConfig(response.config);
          enqueueSnackbar("Mediators information updated successfully", {
            variant: "success",
          });
        }
      } catch (error) {
        const message = getErrorMessage(error);
        enqueueSnackbar(message, { variant: "error" });
      } finally {
        setLoader({ state: false });
      }
    },
    [systemConfig, setLoader, enqueueSnackbar]
  );

  const initialValues = useMemo(
    () => ({
      sections: systemConfig.mediatorsInfo?.length
        ? systemConfig.mediatorsInfo
        : [defaultMediatorObject],
    }), 
    [systemConfig.mediatorsInfo, defaultMediatorObject]
  );

  return (
    <>
      <ActionBar
        breadcrumbs={breadcrumbs}
        onBreadcrumbClick={onBreadcrumbClick}
      />
      <Container>
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validateOnBlur={false}
          validateOnChange={false}
          enableReinitialize
          onSubmit={onFormSubmit}
        >
          {({
            values,
            handleChange,
            errors,
            handleSubmit,
            touched,
            handleBlur,
            setFieldValue,
            setFieldError,
            isSubmitting,
          }) => (
            <FormWrapper onSubmit={handleSubmit}>
              <MediatorsCount>
                {values.sections.length} mediator
                {values.sections.length !== 1 ? "s" : ""} in system
              </MediatorsCount>

              <SectionsContainer>
                {values.sections.map((section, secIndex) => (
                  <MediatorSection
                    key={`mediator-${section._id}-${secIndex}`}
                    section={section}
                    secIndex={secIndex}
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    setFieldError={setFieldError}
                    handleRemove={handleRemove}
                    s3Upload={s3Upload}
                    classes={classes}
                    totalSections={values.sections.length}
                  />
                ))}
              </SectionsContainer>

              <AddMediatorButtonWrapper>
                <AddMediatorButton
                  type="button"
                  onClick={() => addSection(setFieldValue, values.sections)}
                >
                  <AddIcon />
                  <span>Add New Mediator</span>
                </AddMediatorButton>
              </AddMediatorButtonWrapper>

              <SubmitSection>
                <SubmitButtonWrapper>
                  <PrimaryCTAButton
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    style={{ width: "100%", height: "48px" }}
                  >
                    {isSubmitting ? "Saving Changes..." : "Save All Changes"}
                  </PrimaryCTAButton>
                </SubmitButtonWrapper>
              </SubmitSection>
            </FormWrapper>
          )}
        </Formik>
      </Container>
    </>
  );
};

export default Index;

// ============ STYLED COMPONENTS ============

const Container = styled.div`
  padding: 32px 46px;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  min-height: calc(100vh - 80px);
`;

const FormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const MediatorsCount = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  color: #7a8fa6;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const SectionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  && {
    padding: 0 24px;
    min-height: 64px;
  }
`;

const StyledAccordionDetails = styled(AccordionDetails)`
  && {
    padding: 32px 24px;
    background: #fafbfc;
  }
`;

const AccordionWrapper = styled(Accordion)`
  && {
    width: 100%;
  }
`;

const SummaryContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const SummaryIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: rgba(0, 131, 140, 0.1);
  color: #00838c;
  flex-shrink: 0;
`;

const SummaryTexts = styled.div`
  flex: 1;
  min-width: 0;
`;

const SummaryTitle = styled.h3`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 15px;
  color: ${COLORS.COLOR_DARK};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SummarySubtitle = styled.p`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  color: #9ca8b8;
  margin: 4px 0 0 0;
`;

const SummaryBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00838c 0%, #005a65 100%);
  color: white;
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 12px;
  flex-shrink: 0;
`;

const Section = styled.div`
  width: 100%;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e8ecf1;
`;

const SectionTitle = styled.h2`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 16px;
  color: ${COLORS.COLOR_DARK};
  margin: 0;
  letter-spacing: -0.3px;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  animation: fadeInUp 0.3s ease-out;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const FieldLabel = styled(FormLabel)`
  && {
    font-family: ${theme.fonts.primaryFontSemiBold};
    font-size: 11px;
    color: ${({ hasError }) => (hasError ? "#ff6b6b" : "#7a8fa6")};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    font-weight: 600;
    transition: all 0.2s ease;
    margin-bottom: 4px;
  }
`;

const FieldInputWrapper = styled.div`
  position: relative;

  border-radius: 6px;
  background: white;
  transition: all 0.2s ease;
  overflow: hidden;

  &:focus-within {
    border-color: #00838c;
    box-shadow: 0 0 0 1px rgba(0, 131, 140, 0.1);
  }
`;

const ErrorIndicator = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ff6b6b;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
`;

const ErrorMessage = styled.span`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 11px;
  color: #ff6b6b;
  margin-top: 4px;
`;

const FileUploadWrapper = styled.div`
  border-radius: 6px;
  background: ${({ hasError, isFocused }) =>
    hasError
      ? "rgba(255, 107, 107, 0.05)"
      : isFocused
      ? "rgba(0, 131, 140, 0.05)"
      : "#fafbfc"};
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: #00838c;
    background: rgba(0, 131, 140, 0.05);
  }
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  color: white;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(255, 107, 107, 0.3);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    font-size: 16px;
  }
`;

const AddMediatorButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 16px 0;
`;

const AddMediatorButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(
    135deg,
    rgba(0, 131, 140, 0.1) 0%,
    rgba(0, 131, 140, 0.05) 100%
  );
  border: 1px solid #00838c;
  border-radius: 8px;
  color: #00838c;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(0, 131, 140, 0.15) 0%,
      rgba(0, 131, 140, 0.1) 100%
    );
    border-color: #005a65;
    box-shadow: 0 4px 12px rgba(0, 131, 140, 0.15);
  }

  &:active {
    transform: scale(0.98);
  }

  svg {
    font-size: 18px;
  }
`;

const SubmitSection = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e8ecf1;
`;

const SubmitButtonWrapper = styled.div`
  width: 100%;
  max-width: 300px;
`;
